<template>
  <div :class="formVariants({ theme })">
    <div v-if="modelPerson.useLookup">
      <Input
        :label="t('postal-address.name')"
        :placeholder="`${modelMasked.name.first} ${modelMasked.name.last}`"
        :style-input="inputLookupVariants({ theme })"
        class="lg:hidden"
        required
        disabled
      />
      <div class="hidden lg:flex lg:gap-4">
        <Input
          :label="t('postal-address.firstname')"
          :placeholder="`${modelMasked.name.first}`"
          :style-input="inputLookupVariants({ theme })"
          required
          disabled
        />
        <Input
          :label="t('postal-address.lastname')"
          :placeholder="`${modelMasked.name.last}`"
          :style-input="inputLookupVariants({ theme })"
          required
          disabled
        />
      </div>
      <div class="flex flex-col lg:flex-row lg:gap-4">
        <Input
          :label="t('postal-address.street-address')"
          :placeholder="modelMasked.address.street"
          :style-input="inputLookupVariants({ theme })"
          class="grow-[2]"
          required
          disabled
        />
        <Input
          :label="t('postal-address.postcode')"
          :placeholder="modelMasked.address.postcode"
          :style-input="inputLookupVariants({ theme })"
          required
          disabled
        />
      </div>
      <Select
        v-if="props.showCountry"
        :title="t('postal-address.select-country')"
        :style-input="selectLookupVariants({ theme })"
        :options="countries"
        v-model="modelPerson.address.countryCode"
        disabled
        required
      />
    </div>

    <div v-else>
      <div class="flex flex-col lg:flex-row lg:gap-4">
        <Input
          :label="t('postal-address.firstname')"
          v-model="modelPerson.name.first"
          :style-input="inputVariants({ theme })"
          :style-error="inputErrorVariants({ theme })"
          :v$="v$?.name?.first"
          class="mb-7"
          required
        />
        <Input
          :label="t('postal-address.lastname')"
          v-model="modelPerson.name.last"
          :style-input="inputVariants({ theme })"
          :style-error="inputErrorVariants({ theme })"
          :v$="v$?.name?.last"
          class="mb-7"
          required
        />
      </div>
      <div class="flex flex-col lg:flex-row lg:gap-4">
        <Input
          :label="t('postal-address.street-address')"
          v-model="modelPerson.address.street"
          :style-input="inputVariants({ theme })"
          :style-error="inputErrorVariants({ theme })"
          :v$="v$?.address?.street"
          class="mb-7 grow-[2]"
          required
        />
        <Input
          :label="t('postal-address.postcode')"
          v-model="modelPerson.address.postcode"
          :style-input="inputVariants({ theme })"
          :style-error="inputErrorVariants({ theme })"
          :v$="v$?.address?.postcode"
          class="mb-7"
          required
        />
      </div>
      <Input
        v-if="props.showCity"
        :label="t('postal-address.city')"
        v-model="modelPerson.address.city"
        :style-input="inputVariants({ theme })"
        :style-error="inputErrorVariants({ theme })"
        :v$="v$?.address?.city"
        class="mb-7"
        required
      />
      <MultiSelect
        v-if="[THEMES.DEFAULT, THEMES.DJURENS_RATT].includes(theme) && showCountry"
        :title="t('postal-address.select-country')"
        style-input="xl:w-8/12"
        :options="countries"
        :defaultValue="modelPerson.address.countryCode"
        :required="true"
        v-model="modelPerson.address.countryCode"
      />
      <MultiSelectRDK
        v-if="theme === THEMES.RDK && showCountry"
        :title="t('postal-address.select-country')"
        style-input="xl:w-8/12"
        :options="countries"
        :required="true"
        v-model="modelPerson.address.countryCode"
      />
      <div>
        <!-- TODO: Enable this when the backend is ready
        <CheckboxGroup
          :label="t('postal-address.save-data')"
          v-model="modelSavePostalAddress"
          :description="$t('postal-address.save-data.description')"
          class="mt-6"
          :theme="theme"
        /> -->
        <!-- <CheckboxGroupWithOutBg
          :label="t('postal-address.save-data')"
          v-model="modelSavePostalAddress"
          :description="$t('postal-address.save-data.description')"
          class="mt-6"
          :theme="theme"
        /> -->
      </div>
    </div>
    <p v-if="props.isLastStep" class="-mb-4 text-blue lg:mt-4 lg:text-lg">
      {{ t('postal-address.verify-details-message') }}
    </p>
    <Button
      v-if="siteOrg !== 'raddadjuren'"
      :title="props.nextText || t('button.next')"
      color="pink"
      class="mb-8 mt-8 w-full lg:mb-2 lg:mt-10 lg:w-[315px]"
      @click="handleClickNext"
      :arrow-right="true"
    />
    <ButtonAct
      v-if="siteOrg === 'raddadjuren'"
      :title="props.nextText || t('button.next')"
      :theme="theme"
      :isCta="true"
      :variant="'primary'"
      :iconType="'arrowRight'"
      @click="handleClickNext"
    />

    <button
      v-if="modelPerson.useLookup"
      :class="bottomButtonVariants({ theme })"
      @click="handleFillData"
    >
      {{ t('fill-other') }}
    </button>
    <button
      v-if="!modelPerson.useLookup && modelMasked.name.first && modelMasked.name.last"
      :class="bottomButtonVariants({ theme })"
      @click="handleUseData"
    >
      {{ t('use-lookup') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { Input, Button, Select, THEMES, MultiSelect } from 'refresh-ui'
import { cva } from 'class-variance-authority'
import { useVModel } from '@vueuse/core'
import type { Validation } from '@vuelidate/core'
import type { Person } from 'shared-types'

import type { SiteLang, SiteOrg } from '../../types/env'

import MultiSelectRDK from './MultiSelectRDK.vue'

import { useCountries } from '../../composables/useCountries'

import { useI18n } from 'vue-i18n'
import ButtonAct from '../atoms/ButtonAct.vue'

export interface IPostalAddressValidation {
  name: {
    first: string
    last: string
  }
  address: {
    street: string
    postcode: string
    countryCode: string
  }
}

const route = useRoute()
const { t } = useI18n()
const config = useRuntimeConfig()

const lang = (route.query.lang as SiteLang) || config.public.SITE_LANG || 'sv'

const { countries } = useCountries(lang)

const props = withDefaults(
  defineProps<{
    person: Person
    masked: Person
    showCity?: boolean
    showCountry?: boolean
    savePostalAddress: boolean
    v$: Validation<IPostalAddressValidation>
    theme?: THEMES
    nextText?: string
    siteOrg: SiteOrg
    isLastStep?: boolean // Needed for /medlem
  }>(),
  {
    theme: THEMES.DEFAULT,
    showCity: false,
    showCountry: true,
    nextText: '',
    isLastStep: false,
  },
)

const emits = defineEmits(['update:person', 'update:masked', 'update:savePostalAddress', 'onNext'])

const modelPerson = useVModel(props, 'person', emits)
const modelMasked = useVModel(props, 'masked', emits)
const modelSavePostalAddress = useVModel(props, 'savePostalAddress', emits)

const scrollToTop = () => window.scrollTo(0, 0)

const handleFillData = () => {
  scrollToTop()
  modelPerson.value.useLookup = false
}

const handleUseData = () => {
  scrollToTop()
  modelPerson.value.useLookup = true
}

const handleClickNext = () => {
  if (props.v$) {
    props.v$.$touch()
    props.v$.$validate()
    if (!props.v$.$invalid) emits('onNext')
    console.log('Validation failed', props.v$.$errors)
    return
  }
  emits('onNext')
}

const inputLookupVariants = cva('mb-7', {
  variants: {
    theme: {
      [THEMES.DEFAULT]:
        'border border-[#929292] focus:border-blue-dark  shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full bg-[#D8D8D8]',
      [THEMES.RDK]:
        'border border-rdk-orange focus:border-rdk-orange w-full bg-purple-act-bg border-purple-act-border text-purple-act-input shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)]',
      [THEMES.DJURENS_RATT]:
        'border border-[#929292] focus:border-blue-dark  shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full bg-[#D8D8D8]',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const inputVariants = cva('', {
  variants: {
    theme: {
      [THEMES.DEFAULT]:
        'border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full',
      [THEMES.RDK]: 'border border-rdk-orange focus:border-rdk-orange w-full',
      [THEMES.DJURENS_RATT]:
        'border !border-blue-dark focus:border-blue-dark shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] w-full',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const formVariants = cva('xl:max-w-[575px] w-full', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]:
        'text-purple-act-dark [&_label]:text-purple-act-dark font-avenir [&_label]:text-body',
      [THEMES.DJURENS_RATT]: 'pb-10',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const selectLookupVariants = cva('lg:w-8/12', {
  variants: {
    theme: {
      [THEMES.DEFAULT]:
        'border border-[#929292] focus:border-blue-dark  shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] bg-[#D8D8D8]',
      [THEMES.RDK]:
        'border border-rdk-orange focus:border-rdk-orange w-full bg-purple-act-bg border-purple-act-border text-purple-act-input shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] lg:mb-7 lg:w-8/12"',
      [THEMES.DJURENS_RATT]:
        'border border-[#929292] focus:border-blue-dark  shadow-[0_12px_33px_0_rgba(5, 58, 65, 0.04)] bg-[#D8D8D8]',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const subTitleVariants = cva('mb-9 text-base leading-none lg:text-28 xl:mb-14', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'text-black',
      [THEMES.RDK]: ' text-purple-act-dark lg:text-center ',
      [THEMES.DJURENS_RATT]: ' text-black lg:text-center',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const bottomButtonVariants = cva('mt-7 lg:mt-14 lg:w-auto w-full text-center underline', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: 'text-[14px] font-bold text-[#0088A4] lg:text-base',
      [THEMES.RDK]: 'text-base leading-tight font-bold font-avenir text-purple-act-dark',
      [THEMES.DJURENS_RATT]: 'text-[14px] font-bold text-[#0088A4] lg:text-base',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})

const inputErrorVariants = cva('relative bg-transparent', {
  variants: {
    theme: {
      [THEMES.DEFAULT]: '',
      [THEMES.RDK]: 'text-[16px] lg:text-[18px]',
      [THEMES.DJURENS_RATT]: '',
    },
  },
  defaultVariants: {
    theme: THEMES.DEFAULT,
  },
})
</script>
